import MirrorBackButton from "@/components/MirrorBackButton";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import {
  ChevronLeft, Bell, MessageSquare, Settings, Clock,
  Moon, Target, Zap, Calendar, DollarSign, Dumbbell,
  Star, Baby, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { BABY_MODULE_ENABLED } from "@/lib/featureFlags";

// ── Types ────────────────────────────────────────────────────────────────────

interface WhisperDef {
  timeKey: string;
  categoryKey: string;
  tagColor: string;
  icon: React.ElementType;
  iconColor: string;
  textKey: string;
}

interface ToggleDef {
  id: string;
  labelKey: string;
  subKey: string;
  comingSoon?: boolean;
}

// ── Static data ───────────────────────────────────────────────────────────────

const WHISPERS: WhisperDef[] = [
  { timeKey: "whisper.time.sleep", categoryKey: "whisper.cat.sleep", tagColor: "hsl(265 60% 55%)", icon: Moon, iconColor: "hsl(265 60% 65%)", textKey: "whisper.text.sleep" },
  { timeKey: "whisper.time.challenge", categoryKey: "whisper.cat.challenge", tagColor: "hsl(187 72% 45%)", icon: Target, iconColor: "#00e5ff", textKey: "whisper.text.challenge" },
  { timeKey: "whisper.time.energy", categoryKey: "whisper.cat.energy", tagColor: "hsl(40 90% 50%)", icon: Zap, iconColor: "hsl(40 90% 60%)", textKey: "whisper.text.energy" },
  { timeKey: "whisper.time.agenda", categoryKey: "whisper.cat.agenda", tagColor: "hsl(210 70% 55%)", icon: Calendar, iconColor: "hsl(210 70% 65%)", textKey: "whisper.text.agenda" },
  { timeKey: "whisper.time.finance", categoryKey: "whisper.cat.finance", tagColor: "hsl(142 55% 45%)", icon: DollarSign, iconColor: "hsl(142 55% 55%)", textKey: "whisper.text.finance" },
  { timeKey: "whisper.time.fitness", categoryKey: "whisper.cat.fitness", tagColor: "hsl(25 90% 50%)", icon: Dumbbell, iconColor: "hsl(25 90% 60%)", textKey: "whisper.text.fitness" },
  { timeKey: "whisper.time.score", categoryKey: "whisper.cat.score", tagColor: "hsl(187 72% 45%)", icon: Star, iconColor: "#00e5ff", textKey: "whisper.text.score" },
  { timeKey: "whisper.time.baby", categoryKey: "whisper.cat.baby", tagColor: "hsl(330 60% 55%)", icon: Baby, iconColor: "hsl(330 60% 70%)", textKey: "whisper.text.baby" },
].filter((w) => BABY_MODULE_ENABLED || w.textKey !== "whisper.text.baby");

const TOGGLES: ToggleDef[] = [
  { id: "morning",   labelKey: "whisper.toggle.morning",   subKey: "whisper.toggle.morningSub" },
  { id: "evening",   labelKey: "whisper.toggle.evening",   subKey: "whisper.toggle.eveningSub" },
  { id: "challenge", labelKey: "whisper.toggle.challenge", subKey: "whisper.toggle.challengeSub" },
  { id: "energy",    labelKey: "whisper.toggle.energy",    subKey: "whisper.toggle.energySub" },
  { id: "sleep",     labelKey: "whisper.toggle.sleep",     subKey: "whisper.toggle.sleepSub" },
  { id: "agenda",    labelKey: "whisper.toggle.agenda",    subKey: "whisper.toggle.agendaSub", comingSoon: true },
  { id: "finance",   labelKey: "whisper.toggle.finance",   subKey: "whisper.toggle.financeSub" },
];

const DEFAULT_TOGGLES: Record<string, boolean> = {
  morning: true, evening: true, challenge: true,
  energy: true, sleep: true, agenda: false, finance: true,
};

const MORNING_TIMES = ["06:30", "07:00", "07:30"];
const EVENING_TIMES = ["20:00", "21:00", "22:00"];

const stagger = (i: number) => ({ delay: 0.06 + i * 0.07, duration: 0.5 });

// ── Toggle Switch ─────────────────────────────────────────────────────────────

const ToggleSwitch = ({
  on, onChange, disabled,
}: { on: boolean; onChange: () => void; disabled?: boolean }) => (
  <button
    onClick={disabled ? undefined : onChange}
    className="relative flex-shrink-0 w-11 h-6 rounded-full transition-all duration-300"
    style={{
      background: on ? "#00e5ff" : "hsl(218 14% 25%)",
      opacity: disabled ? 0.4 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    <div
      className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-300"
      style={{ left: on ? "calc(100% - 22px)" : "2px" }}
    />
  </button>
);

// ── Page ─────────────────────────────────────────────────────────────────────

const MirrorWhisper = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [toggles, setToggles] = useState<Record<string, boolean>>(DEFAULT_TOGGLES);
  const [morningTime, setMorningTime] = useState("07:00");
  const [eveningTime, setEveningTime] = useState("21:00");

  const handleToggle = (id: string) => {
    setToggles((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleTimeChange = (type: "morning" | "evening", time: string) => {
    if (type === "morning") setMorningTime(time);
    else setEveningTime(time);
    toast(t("whisper.timeUpdated"));
  };

  return (
    <MobileLayout className="pb-28 pt-6 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }}
        />
      </div>

      {/* Back + header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center gap-3 mb-1"
      >
        <MirrorBackButton />
        <div className="flex items-center gap-2">
          <Bell size={13} className="text-primary" />
          <p className="text-[9px] font-display tracking-[0.22em] uppercase"
            style={{ color: "hsl(187 72% 50% / 0.6)" }}>
            {t("whisper.smartNotifications")}
          </p>
        </div>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.05 }}
        className="font-display text-[26px] font-semibold text-foreground leading-none mb-6"
      >
        {t("whisper.title")}
      </motion.h1>

      {/* ── HERO WHISPER CARD ── */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(1)}
        className="rounded-2xl p-5 mb-6 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0d1117 0%, #1a1a2e 100%)",
          border: "1px solid rgba(0,229,255,0.40)",
          boxShadow: "0 0 30px rgba(0,229,255,0.10), 0 8px 32px rgba(0,0,0,0.4)",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/35 to-transparent" />

        {/* Badge + time */}
        <div className="flex items-center justify-between mb-3">
          <span
            className="text-[9px] font-display tracking-[0.2em] uppercase px-2 py-0.5 rounded-full"
            style={{ color: "hsl(var(--primary))", background: "rgba(0,229,255,0.10)", border: "1px solid rgba(0,229,255,0.25)" }}
          >
            {t("whisper.lastWhisper")}
          </span>
          <span className="text-[10px] font-display" style={{ color: "hsl(218 14% 50%)" }}>
            {t("whisper.heroTime")}
          </span>
        </div>

        {/* Message */}
        <p className="text-[14px] font-body text-foreground/85 leading-relaxed italic mb-4">
          {t("whisper.heroMessage")}
        </p>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/mirror")}
            className="flex items-center gap-1 px-3 py-1.5 rounded-xl font-display text-[11px] font-semibold tracking-wide transition-all active:scale-[0.96]"
            style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}
          >
            {t("whisper.openMirror")}
          </button>
          <button
            onClick={() => navigate("/sleep")}
            className="flex items-center gap-1 px-3 py-1.5 rounded-xl font-display text-[11px] font-semibold tracking-wide transition-all active:scale-[0.96]"
            style={{ background: "transparent", border: "1px solid rgba(0,229,255,0.35)", color: "hsl(var(--primary))" }}
          >
            {t("whisper.logSleep")}
          </button>
          <button
            onClick={() => toast(t("whisper.dismissed"))}
            className="px-3 py-1.5 rounded-xl font-display text-[11px] font-medium transition-all active:scale-[0.96]"
            style={{ color: "hsl(218 14% 50%)", border: "1px solid hsl(218 14% 22%)" }}
          >
            {t("whisper.dismiss")}
          </button>
        </div>
      </motion.div>

      {/* ── WHISPER FEED ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(2)}
        className="mb-6"
      >
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={13} style={{ color: "hsl(var(--primary))" }} />
          <p className="text-[9px] font-display tracking-[0.22em] uppercase"
            style={{ color: "hsl(187 72% 50% / 0.6)" }}>
            {t("whisper.feed")}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {WHISPERS.map((w, i) => {
            const Icon = w.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.05, duration: 0.4 }}
                className="rounded-2xl p-3.5"
                style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.13)" }}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div
                    className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: `${w.iconColor}18`, border: `1px solid ${w.iconColor}30` }}
                  >
                    <Icon size={14} style={{ color: w.iconColor }} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span
                        className="text-[8px] font-display tracking-[0.14em] font-bold px-1.5 py-0.5 rounded"
                        style={{ color: w.tagColor, background: `${w.tagColor}18`, border: `1px solid ${w.tagColor}30` }}
                      >
                        {t(w.categoryKey)}
                      </span>
                      <span className="text-[9px] font-display shrink-0" style={{ color: "hsl(218 14% 42%)" }}>
                        {t(w.timeKey)}
                      </span>
                    </div>
                    <p className="text-[12px] font-body italic leading-relaxed" style={{ color: "hsl(210 20% 75%)" }}>
                      {t(w.textKey)}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* ── WHISPER SETTINGS ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(3)}
        className="mb-6"
      >
        <div className="flex items-center gap-2 mb-3">
          <Settings size={13} style={{ color: "hsl(var(--primary))" }} />
          <p className="text-[9px] font-display tracking-[0.22em] uppercase"
            style={{ color: "hsl(187 72% 50% / 0.6)" }}>
            {t("whisper.settings")}
          </p>
        </div>

        <div
          className="rounded-2xl overflow-hidden"
          style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
        >
          {TOGGLES.map((toggle, i) => (
            <div
              key={toggle.id}
              className="flex items-center justify-between px-4 py-3.5"
              style={{
                borderBottom: i < TOGGLES.length - 1 ? "1px solid hsl(187 72% 50% / 0.08)" : "none",
              }}
            >
              <div className="flex-1 min-w-0 mr-3">
                <div className="flex items-center gap-2">
                  <p className="font-display text-[13px] font-semibold text-foreground">{t(toggle.labelKey)}</p>
                  {toggle.comingSoon && (
                    <span
                      className="text-[8px] font-display tracking-wide px-1.5 py-0.5 rounded"
                      style={{ color: "hsl(40 90% 60%)", background: "hsl(40 90% 50% / 0.12)", border: "1px solid hsl(40 90% 50% / 0.25)" }}
                    >
                      {t("whisper.soon")}
                    </span>
                  )}
                </div>
                <p className="text-[10px] font-body mt-0.5" style={{ color: "hsl(218 14% 50%)" }}>
                  {t(toggle.subKey)}
                </p>
              </div>
              <ToggleSwitch
                on={toggles[toggle.id]}
                onChange={() => handleToggle(toggle.id)}
                disabled={toggle.comingSoon}
              />
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── DELIVERY TIME ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(4)}
        className="mb-6"
      >
        <div className="flex items-center gap-2 mb-3">
          <Clock size={13} style={{ color: "hsl(var(--primary))" }} />
          <p className="text-[9px] font-display tracking-[0.22em] uppercase"
            style={{ color: "hsl(187 72% 50% / 0.6)" }}>
            {t("whisper.whenShould")}
          </p>
        </div>

        <div
          className="rounded-2xl p-4"
          style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
        >
          {/* Morning */}
          <p className="text-[10px] font-display font-semibold text-foreground mb-2.5 flex items-center gap-1.5">
            🌅 {t("whisper.morning")}
          </p>
          <div className="flex gap-2 mb-5">
            {MORNING_TIMES.map((t) => (
              <button
                key={t}
                onClick={() => handleTimeChange("morning", t)}
                className="flex-1 py-2.5 rounded-xl font-display text-[12px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                style={{
                  background: morningTime === t ? "#00e5ff" : "transparent",
                  border: morningTime === t ? "1.5px solid #00e5ff" : "1px solid hsl(187 72% 50% / 0.22)",
                  color: morningTime === t ? "#0a0a1a" : "hsl(218 14% 60%)",
                }}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Evening */}
          <p className="text-[10px] font-display font-semibold text-foreground mb-2.5 flex items-center gap-1.5">
            🌙 {t("whisper.evening")}
          </p>
          <div className="flex gap-2">
            {EVENING_TIMES.map((t) => (
              <button
                key={t}
                onClick={() => handleTimeChange("evening", t)}
                className="flex-1 py-2.5 rounded-xl font-display text-[12px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                style={{
                  background: eveningTime === t ? "#00e5ff" : "transparent",
                  border: eveningTime === t ? "1.5px solid #00e5ff" : "1px solid hsl(187 72% 50% / 0.22)",
                  color: eveningTime === t ? "#0a0a1a" : "hsl(218 14% 60%)",
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── MIRROR CARD ── */}
      {/* HIDDEN: fake Mirror Insight — re-wire with real Padrão B per audit 23/04 (memory #23) */}
      {false && (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(5)}
        className="rounded-2xl p-4 mb-5 relative overflow-hidden"
        style={{
          background: "linear-gradient(rgba(0,0,0,0.58), rgba(0,0,0,0.72)), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_07.jpeg) center/cover no-repeat",
          border: "1px solid hsl(187 72% 50% / 0.28)",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        <div className="flex items-start gap-3">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.22)" }}
          >
            <Star size={14} style={{ color: "hsl(var(--primary))" }} />
          </div>
          <div className="flex-1">
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1.5"
              style={{ color: "hsl(187 72% 50% / 0.55)" }}>
              {t("whisper.mirrorLabel")}
            </p>
            <p className="text-[12px] font-body text-foreground/65 leading-relaxed mb-3">
              {t("whisper.mirrorText")}
            </p>
            <button
              onClick={() => navigate("/mirror")}
              className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide opacity-75 hover:opacity-100 transition-opacity"
              style={{ color: "hsl(187 72% 55%)" }}
            >
              <span>{t("whisper.talkToMirror")}</span>
              <ArrowRight size={11} />
            </button>
          </div>
        </div>
      </motion.div>
      )}

    </MobileLayout>
  );
};

export default MirrorWhisper;
